import { motion } from "framer-motion";
import { Plus, Minus, Users, Baby, X, Bed } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface GuestPickerProps {
  guests: { adults: number; children: number; rooms: number; childAges?: number[] };
  onChange: (guests: { adults: number; children: number; rooms: number; childAges?: number[] }) => void;
  onClose: () => void;
}

export const GuestPicker = ({ guests, onChange, onClose }: GuestPickerProps) => {
  const [childAges, setChildAges] = useState<number[]>(
    guests.childAges || (guests.children > 0 ? Array(guests.children).fill(5) : [])
  );

  const updateGuest = (field: 'adults' | 'children' | 'rooms', increment: boolean) => {
    const value = guests[field] as number;
    const newValue = increment ? value + 1 : value - 1;
    
    // Validation with user feedback
    if (field === "adults" && newValue < 1) return;
    if (field === "children" && newValue < 0) return;
    if (field === "rooms" && newValue < 1) return;
    
    // ETG Limits with warnings
    if (field === "adults" && newValue > 6) {
      toast.warning("Maximum 6 adults allowed per booking");
      return;
    }
    if (field === "children" && newValue > 4) {
      toast.warning("Maximum 4 children allowed per booking");
      return;
    }
    if (field === "rooms" && newValue > 5) {
      toast.warning("Maximum 5 rooms allowed per booking");
      return;
    }
    
    // Total guest limit: adults + children must be ≤ 10
    if (field === "adults" || field === "children") {
      const currentAdults = field === "adults" ? newValue : guests.adults;
      const currentChildren = field === "children" ? newValue : guests.children;
      if (currentAdults + currentChildren > 10) {
        toast.warning("Maximum 10 total guests allowed per booking");
        return;
      }
    }

    // Handle child ages when children count changes
    if (field === "children") {
      if (increment) {
        const newAges = [...childAges, 5];
        setChildAges(newAges);
        onChange({ ...guests, [field]: newValue, childAges: newAges });
      } else {
        const newAges = childAges.slice(0, -1);
        setChildAges(newAges);
        onChange({ ...guests, [field]: newValue, childAges: newAges });
      }
    } else {
      onChange({ ...guests, [field]: newValue, childAges });
    }
  };

  const updateChildAge = (index: number, age: number) => {
    const newAges = [...childAges];
    newAges[index] = age;
    setChildAges(newAges);
    onChange({ 
      adults: guests.adults, 
      children: guests.children, 
      rooms: guests.rooms, 
      childAges: newAges 
    });
  };

  const handleButtonClick = (e: React.MouseEvent, field: 'adults' | 'children' | 'rooms', increment: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    updateGuest(field, increment);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
      role="form"
      aria-label="Guest selection"
    >

      {/* Adults */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center justify-between py-3"
      >
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-gold" aria-hidden="true" />
          <div>
            <p className="font-medium text-ivory" id="adults-label">Adults</p>
            <p className="text-xs text-ivory/60">Ages 18+</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'adults', false)}
            disabled={guests.adults <= 1}
            className="h-8 w-8 rounded-full border border-gold/30 flex items-center justify-center hover:bg-gold/10 hover:border-gold transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-charcoal"
            aria-label="Decrease adults"
            aria-describedby="adults-label"
            aria-controls="adults-count"
          >
            <Minus className="h-4 w-4 text-ivory" aria-hidden="true" />
          </button>
          <span 
            className="min-w-[2.5rem] text-center font-semibold text-ivory text-lg" 
            id="adults-count"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {guests.adults}
          </span>
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'adults', true)}
            disabled={guests.adults >= 6}
            className="h-8 w-8 rounded-full border border-gold/30 flex items-center justify-center hover:bg-gold/10 hover:border-gold transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-charcoal"
            aria-label="Increase adults"
            aria-describedby="adults-label"
            aria-controls="adults-count"
          >
            <Plus className="h-4 w-4 text-ivory" aria-hidden="true" />
          </button>
        </div>
      </motion.div>

      {/* Children */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="flex items-center justify-between py-3 border-t border-ivory/10"
      >
        <div className="flex items-center gap-3">
          <Baby className="h-5 w-5 text-gold" aria-hidden="true" />
          <div>
            <p className="font-medium text-ivory" id="children-label">Children</p>
            <p className="text-xs text-ivory/60">Ages 0-17</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'children', false)}
            disabled={guests.children <= 0}
            className="h-8 w-8 rounded-full border border-gold/30 flex items-center justify-center hover:bg-gold/10 hover:border-gold transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-charcoal"
            aria-label="Decrease children"
            aria-describedby="children-label"
            aria-controls="children-count"
          >
            <Minus className="h-4 w-4 text-ivory" aria-hidden="true" />
          </button>
          <span 
            className="min-w-[2.5rem] text-center font-semibold text-ivory text-lg"
            id="children-count"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {guests.children}
          </span>
          <button
            type="button"
            onClick={(e) => handleButtonClick(e, 'children', true)}
            disabled={guests.children >= 4}
            className="h-8 w-8 rounded-full border border-gold/30 flex items-center justify-center hover:bg-gold/10 hover:border-gold transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-charcoal"
            aria-label="Increase children"
            aria-describedby="children-label"
            aria-controls="children-count"
          >
            <Plus className="h-4 w-4 text-ivory" aria-hidden="true" />
          </button>
        </div>
      </motion.div>

      {/* Child Ages */}
      {guests.children > 0 && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-3 pt-4 border-t border-gold/20"
        >
          <div className="text-sm font-medium text-ivory flex items-center gap-2">
            <Baby className="w-4 h-4 text-gold" />
            Child ages (required)
          </div>
          {childAges.map((age, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center gap-3"
            >
              <span className="text-xs text-muted-foreground w-20">Child {index + 1}</span>
              <div className="relative flex-1">
                <select
                  value={age}
                  onChange={(e) => updateChildAge(index, parseInt(e.target.value))}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  className="w-full px-4 py-2.5 bg-charcoal/80 backdrop-blur-md border border-gold/30 rounded-xl text-sm text-ivory appearance-none cursor-pointer hover:border-gold focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23D5B167' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem',
                    colorScheme: 'dark'
                  }}
                >
                  {Array.from({ length: 18 }, (_, i) => (
                    <option key={i} value={i} className="bg-charcoal text-ivory py-2">
                      {i} {i === 1 ? 'year' : 'years'}
                    </option>
                  ))}
                </select>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Rooms */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex items-center justify-between py-3 border-t border-gold/10"
      >
        <div className="flex items-center gap-3">
          <Bed className="w-5 h-5 text-gold flex-shrink-0" />
          <div>
            <div className="font-medium text-ivory">Rooms</div>
            <div className="text-xs text-ivory/50">
              {guests.rooms > 1 
                ? `${guests.adults + guests.children} guests across ${guests.rooms} rooms`
                : 'Max 5 rooms'
              }
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.9 }}
            onClick={(e) => handleButtonClick(e, "rooms", false)}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={guests.rooms <= 1}
            className="w-9 h-9 rounded-full border-gold/30 bg-charcoal/60 hover:bg-gold/20 hover:border-gold disabled:opacity-30 transition-all duration-150 border flex items-center justify-center"
            aria-label="Decrease rooms"
          >
            <Minus className="w-4 h-4 text-gold" />
          </motion.button>
          <motion.span 
            key={`rooms-${guests.rooms}`}
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="w-12 text-center font-bold text-xl text-ivory"
          >
            {guests.rooms}
          </motion.span>
          <motion.button
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.9 }}
            onClick={(e) => handleButtonClick(e, "rooms", true)}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={guests.rooms >= 5}
            className="w-9 h-9 rounded-full border-gold/30 bg-charcoal/60 hover:bg-gold/20 hover:border-gold disabled:opacity-30 transition-all duration-150 border flex items-center justify-center"
            aria-label="Increase rooms"
          >
            <Plus className="w-4 h-4 text-gold" />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};
